"use client";

import { ChevronRight, Lock } from "lucide-react";
import type { DiscoveryBookableCard } from "@/actions/venue-discovery";
import { DiscoveryImpression } from "@/components/discovery/discovery-impression";
import { sortMediaByOrder } from "@/components/event-card/event-card.shared";
import { AvailabilityRibbon } from "@/components/ui/availability-ribbon";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Card } from "@/components/ui/card";
import { ExperienceMediaFrame } from "@/components/venue-booking/experience-media-frame.client";
import { getIconComponent, type IconType } from "@/lib/icon-registry";
import { formatLocationDistance } from "@/lib/locations/nearby-location";
import { mediaPreviewUrlFromAsset, mediaUrlFromAsset } from "@/lib/media";
import { cn } from "@/lib/utils";
import {
  allTimesTitle,
  bookableSlotHref,
  experiencePageHref,
  moreOptions,
  slotRowLabel,
} from "./bookable-now.shared";

interface Props {
  card: DiscoveryBookableCard;
  /**
   * `large` is the For You rail and anywhere a card owns ~two thirds of the
   * screen; `default` is the browse grid, where two or four sit side by side.
   * Matches `EventCard`'s own sizes so the two rails read as one system.
   */
  size?: "default" | "large";
  /** Drop the per-card volume control. On in grids, where nobody owns audio. */
  forceMutedMedia?: boolean;
  priorityMedia?: boolean;
  className?: string;
}

/**
 * One bookable experience, as a card you would actually want to tap.
 *
 * This replaces a green-washed panel that nested a venue photo inside a venue
 * row inside an activity box: three borders deep, it read as an admin table
 * sitting next to the event rail, and people skipped it. The anatomy here is
 * the event card's — full-bleed venue media, a floating glass logo, a tight
 * metadata block — with one addition the event card has no need for: the next
 * starts as buttons you can press straight into, because a slot is a time and
 * making somebody open a page to choose one is the friction that kept these
 * sessions empty.
 *
 * The media is the experience's own gallery at the event card's 4:5. It used
 * to be the venue banner at 16:10, which suited a landscape shot of a
 * building; experience media is shot portrait (9:16 reels, 4:5 stills), and a
 * 16:10 letterbox of one is a strip of somebody's torso. The building now
 * rides along as a small thumbnail in the bottom-left corner, where the event
 * card would print its location.
 */
export function BookableSlotCard({
  card,
  size = "default",
  forceMutedMedia = false,
  priorityMedia = false,
  className,
}: Props) {
  const isLarge = size === "large";
  const orderedMedia = sortMediaByOrder(card.bannerMedia);
  const primaryMedia = orderedMedia[0] ?? null;
  const ActivityIcon = getIconComponent(card.activityIconKey as IconType);

  const nextSlot = card.slots[0];
  const more = moreOptions(card);
  const logoUrl = mediaUrlFromAsset(card.hiiveLogoImage);
  // Poster, never the binary: a venue whose lead item is a video would
  // otherwise hand an <img> an mp4.
  const venueImageUrl = mediaPreviewUrlFromAsset(card.venueImage);
  const experienceHref = nextSlot
    ? bookableSlotHref(card, nextSlot.startIso)
    : experiencePageHref(card);

  // EventCard's type scale and spacing, so the two rails read as one system.
  const labelClass = isLarge ? "text-[13px]" : "text-[10px]";
  const titleClass = isLarge ? "text-base" : "text-xs";
  const activityIconSize = isLarge ? "h-6 w-6" : "h-4.5 w-4.5";
  const activityIconInner = isLarge ? "h-3.5 w-3.5" : "h-2.5 w-2.5";
  const padding = isLarge ? "px-3" : "px-2";

  return (
    <Card
      className={cn(
        // One surface, edge to edge. The times used to sit on their own tinted
        // band with a rule above it, which split the card in two colours for
        // no reason anybody reading it could name.
        "group relative flex h-full w-full flex-col gap-0 overflow-hidden rounded-3xl border border-border bg-surface p-0",
        "transition-colors duration-200 ease-[var(--ease-out-strong)]",
        "hover:border-brand/40 dark:hover:border-white/20",
        className,
      )}
    >
      <DiscoveryImpression
        entityType="class"
        entityId={String(card.experienceId)}
        surface={card.discoverySurface ?? "hiives"}
        token={card.discoveryToken}
      />
      {card.discovery?.tier === "market" ? (
        <p className="px-3 py-1 text-xs text-text-secondary">
          {card.discovery.label}
        </p>
      ) : null}
      <BackAwareLink
        // The card body lands on the next start, already selected. The time
        // buttons below are siblings, not children: an anchor inside an anchor
        // is invalid markup and browsers resolve it by dropping one of them.
        href={experienceHref}
        className="flex min-w-0 flex-1 flex-col"
        aria-label={
          nextSlot
            ? `${card.experienceName} at ${card.venueName}, ${nextSlot.dayLabel} ${nextSlot.timeLabel}`
            : `${card.experienceName} at ${card.venueName}`
        }
      >
        {/* Where: the venue's own picture and name, bottom-left on the media
            — where the event card would print its location. Every card in a
            hiive's own tab names the same venue, so a metadata row for it
            under the photo made the stack read as a repeat. */}
        <div className="relative">
          <AvailabilityRibbon availability={card.availability} />
          <ExperienceMediaFrame
            media={primaryMedia ? orderedMedia : []}
            label={`${card.experienceName} at ${card.venueName}`}
            hiiveName={card.hiiveName}
            hiiveLogoUrl={logoUrl}
            venueName={card.venueName}
            venueImageUrl={venueImageUrl}
            venueSubtitle={
              card.distanceKm != null
                ? `${formatLocationDistance(card.distanceKm)} away`
                : null
            }
            size={size}
            forceMuted={forceMutedMedia}
            music={card.bannerMusic}
            priority={priorityMedia}
            fallback={
              ActivityIcon ? (
                // Opacity on the wrapper, not the icon: the activity SVGs
                // hard-code their brand fill and ignore currentColor.
                <div className="opacity-30" aria-hidden="true">
                  <ActivityIcon className={isLarge ? "h-10 w-10" : "h-7 w-7"} />
                </div>
              ) : null
            }
            badges={
              card.membersOnly ? (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border border-glass-border-10 bg-[rgba(0,0,0,0.45)] font-medium text-white backdrop-blur-md",
                    isLarge ? "h-8 px-2.5 text-[11px]" : "h-6 px-2 text-[9px]",
                  )}
                >
                  <Lock
                    className={isLarge ? "h-3 w-3" : "h-2.5 w-2.5"}
                    aria-hidden="true"
                  />
                  Members
                </span>
              ) : null
            }
          />
        </div>

        <div
          className={cn(
            // `flex-1`: a card with fewer rows than its neighbour stretches this
            // panel, not a strip of the root, which is a different colour.
            "flex flex-1 flex-col text-text-primary dark:text-white",
            PANEL,
            isLarge ? "gap-1 px-3 pt-3 pb-2.5" : "gap-0.5 px-2 pt-2 pb-2",
          )}
        >
          {/* How much and what, on one line, with the activity's icon at its
              end. A separate price row under the title made the card three
              bands of small grey text. */}
          <div className="flex items-center justify-between gap-1.5">
            <span
              className={cn(
                "min-w-0 flex-1 truncate font-medium text-text-tertiary dark:text-white/70",
                labelClass,
              )}
            >
              {/* Price first, then what it is. The day already rides on the
                  Book row's eyebrow, so repeating it here said "Today" twice
                  within an inch. */}
              {`${formatPrice(card)} · ${card.activityName}`}
            </span>
            {ActivityIcon ? (
              <div
                className={cn(
                  "flex shrink-0 items-center justify-center rounded-full border border-border bg-surface-alt dark:border-glass-border-10 dark:bg-glass-bg-10",
                  activityIconSize,
                )}
                title={card.activityName}
              >
                <ActivityIcon
                  className={cn(
                    "text-text-secondary dark:text-white",
                    activityIconInner,
                  )}
                />
              </div>
            ) : null}
          </div>
          <h3
            className={cn("truncate leading-tight font-semibold", titleClass)}
          >
            {card.experienceName}
          </h3>
        </div>
      </BackAwareLink>

      {nextSlot ? (
        // Exactly one bookable row on every card, then one "more" link. A
        // stack of rows per card left a one-time card with a hole where its
        // neighbour had a second row, and read as a form, not a card.
        <div
          className={cn(
            // `mt-auto` pins the footer to the card's bottom, which is what
            // levels it across a grid row of uneven titles.
            "mt-auto flex flex-col",
            PANEL,
            padding,
            isLarge ? "gap-1 pb-2" : "gap-0.5 pb-1.5",
          )}
        >
          <BackAwareLink
            href={
              nextSlot.soldOut
                ? experiencePageHref(card)
                : bookableSlotHref(card, nextSlot.startIso)
            }
            className={cn(
              SLOT_ROW,
              isLarge
                ? "h-12 gap-2 pr-1.5 pl-3 text-[13px]"
                : "h-10 gap-1 pr-1 pl-2 text-[10.5px]",
            )}
            // "All day" stands in for the hours, so they stay reachable.
            title={nextSlot.rangeLabel}
            aria-label={`${nextSlot.soldOut ? "View sold out session" : "Book"} ${card.experienceName}, ${nextSlot.dayLabel} ${nextSlot.rangeLabel}`}
          >
            {/* The day rides on the time itself. Said only on the line above
                the title, "6:30 – 7:30 AM" read as a time of no particular
                day; the eyebrow keeps it from being tomorrow's by accident. */}
            <span className="flex min-w-0 flex-col leading-tight">
              <span
                className={cn(
                  "truncate font-semibold tracking-wide text-brand uppercase dark:text-violet-300",
                  isLarge ? "text-[10px]" : "text-[8px]",
                )}
              >
                {nextSlot.dayLabel}
              </span>
              <span className="truncate">{slotRowLabel(nextSlot)}</span>
            </span>
            <span
              className={cn(
                BOOK_CHIP,
                isLarge ? "h-7 px-3 text-[12px]" : "h-6 px-2 text-[10px]",
              )}
              aria-hidden="true"
            >
              {nextSlot.soldOut ? "Sold out" : "Book"}
            </span>
          </BackAwareLink>

          {/* Always rendered, so every card's footer is the same height. The
              fact on the left says there is a choice; the action on the right
              says where to make it. */}
          <BackAwareLink
            href={experiencePageHref(card)}
            title={allTimesTitle(card)}
            className={cn(
              MORE_LINK,
              isLarge
                ? "h-7 gap-2 px-3 text-[12px]"
                : "h-5 gap-0.5 px-1 text-[10px]",
            )}
          >
            <span className="min-w-0 truncate">{more.info}</span>
            <span className="inline-flex shrink-0 items-center font-semibold text-brand dark:text-violet-300">
              {more.cta}
              <ChevronRight
                className={isLarge ? "h-3.5 w-3.5" : "h-3 w-3"}
                aria-hidden="true"
              />
            </span>
          </BackAwareLink>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * The event card's own content fill.
 *
 * `Card`'s base `dark:bg-black/35` beats a plain `bg-surface` in the class
 * merge — a dark variant and an unvariant utility are different groups — so the
 * card root is *not* the colour it names, and `EventCard` lands on its surface
 * by painting this over the root. Both blocks below carry it so this card is
 * one continuous colour and the same colour as the rail above it.
 */
const PANEL = "bg-surface/90 dark:bg-surface/80";

/**
 * The next start, as something you can book: the window on the left, the
 * action on the right. The row is the tap target; the Book chip names what the
 * tap does, which a bare time never said.
 */
const SLOT_ROW = cn(
  "flex w-full items-center justify-between rounded-xl font-semibold tabular-nums",
  "border border-brand/30 bg-brand/[0.06] text-text-primary",
  "dark:border-brand/45 dark:bg-brand/[0.14] dark:text-white",
  "transition-[transform,background-color,border-color] duration-150 ease-[var(--ease-out-strong)]",
  "hover:border-brand/55 hover:bg-brand/[0.12] active:scale-[0.97]",
  "dark:hover:border-brand/70 dark:hover:bg-brand/[0.22]",
);

/** The way to the other options. Quieter than the Book row, which stays the offer. */
const MORE_LINK = cn(
  "flex w-full items-center justify-between rounded-lg font-medium tabular-nums",
  "text-text-secondary dark:text-white/60",
  "transition-opacity duration-150 ease-[var(--ease-out-strong)]",
  "hover:opacity-80 active:opacity-60",
);

const BOOK_CHIP = cn(
  "inline-flex shrink-0 items-center justify-center rounded-lg font-semibold",
  "bg-brand text-white dark:bg-brand",
);

const formatAmount = (amountMinor: number, currency: string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0,
  }).format(amountMinor / 100);

/**
 * "₹840", "From ₹840", "₹500 / 60 min" or "Free".
 *
 * "From" only when another ticket or free spot at that start costs more — on
 * a single price it reads as a hidden upcharge. The duration is load-bearing
 * on a court you rent by the half hour: without it "₹500" reads as the price
 * of the evening, and the booking page then looks like it put the price up.
 */
function formatPrice(card: DiscoveryBookableCard): string {
  if (card.fromPriceMinor <= 0) return "Free";
  const amount = formatAmount(card.fromPriceMinor, card.currency);
  const price = card.priceVaries ? `From ${amount}` : amount;
  return card.priceDurationLabel
    ? `${price} / ${card.priceDurationLabel}`
    : price;
}
